import {
  AuthenticationDetails,
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser
} from 'amazon-cognito-identity-js'

export default class CognitoLogin {
  // create a cognito user pool object
  static createCognitoUserPool (unauth = false) {
    var poolData
    if (unauth) {
      poolData = {
        UserPoolId: window.CONFIG.UnauthUserPoolId,
        ClientId: window.CONFIG.UnauthUserPoolClientId
      }
    } else {
      poolData = {
        UserPoolId: window.CONFIG.UserPoolId,
        ClientId: window.CONFIG.UserPoolClientId
      }
    }
    var userPool = new CognitoUserPool(poolData)
    return userPool
  }

  // create a cognito user
  static createCognitoUser (userName, unauth = false) {
    var userPool = this.createCognitoUserPool(unauth)
    var userData = {
      Username: userName,
      Pool: userPool
    }
    var cognitoUser = new CognitoUser(userData)
    return cognitoUser
  }

  // Return the cognito user from local storage if they exist
  static getCognitoUser (unauth = false) {
    var userPool = this.createCognitoUserPool(unauth)
    var cognitoUser = userPool.getCurrentUser()
    return (cognitoUser !== null) ? cognitoUser : null
  }

  // sign up the user with Cognito User Pools
  static signUpCognito (authData, unauth = false) {
    return new Promise((resolve, reject) => {
      var userPool = this.createCognitoUserPool(unauth)
      var attributeList = []
      var dataEmail = {
        Name: authData.loginType,
        Value: authData.userName
      }
      var attributeEmail = new CognitoUserAttribute(dataEmail)

      attributeList.push(attributeEmail)
      userPool.signUp(authData.userName, authData.password, attributeList, null, function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  // input the verification code from userName
  static confirmRegistrationCognito (userName, confirmationCode) {
    return new Promise((resolve, reject) => {
      var cognitoUser = this.createCognitoUser(userName)
      cognitoUser.confirmRegistration(confirmationCode, true, function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  // Log user in with Cognito User Pools
  static loginCognito (userName, password) {
    return new Promise((resolve, reject) => {
      var authenticationData = {
        Username: userName,
        Password: password
      }
      var authenticationDetails = new AuthenticationDetails(authenticationData)
      var cognitoUser = this.getCognitoUser()
      if (cognitoUser === null) {
        cognitoUser = this.createCognitoUser(userName)
      }
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess (result) {
          return resolve(result)
        },
        onFailure (error) {
          return reject(error)
        },
        mfaRequired: function (codeDeliveryDetails) {
          var verificationCode = prompt('Please input verification code', '')
          cognitoUser.sendMFACode(verificationCode, this)
        }
      })
    })
  }

  // unauthenticated login
  static unauthLoginCognito (userName, password) {
    return new Promise((resolve, reject) => {
      var authenticationData = {
        Username: userName,
        Password: password
      }
      var authenticationDetails = new AuthenticationDetails(authenticationData)
      var cognitoUser = this.getCognitoUser(true)
      if (cognitoUser === null) {
        cognitoUser = this.createCognitoUser(userName, true)
      }
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess (result) {
          return resolve(result)
        },
        onFailure (error) {
          return reject(error)
        }
      })
    })
  }

  // Initiate forgot password logic. This invalidates the password and
  // sends a confirmation userNames to the user.
  static initiateForgotPasswordCognito (userName) {
    return new Promise((resolve, reject) => {
      var cognitoUser = this.createCognitoUser(userName)
      cognitoUser.forgotPassword({
        onSuccess (result) {
          return resolve(result)
        },
        onFailure (error) {
          return reject(error)
        }
      })
    })
  }

  // This function actually changes the users password to a new password
  static concludeForgotPassword (userName, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      var cognitoUser = this.createCognitoUser(userName)
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess (result) {
          return resolve(result)
        },
        onFailure (error) {
          return reject(error)
        }
      })
    })
  }

  static getCognitoUserSession (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession(function (error, session) {
        return (error) ? reject(error) : resolve(session)
      })
    })
  }

  static changePasswordWrapper (cognitoUser, oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      cognitoUser.changePassword(oldPassword, newPassword, function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async changePasswordCognito (oldPassword, newPassword) {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.changePasswordWrapper(cognitoUser, oldPassword, newPassword)
  }

  static refreshSessionWrapper (cognitoUser, session) {
    return new Promise((resolve, reject) => {
      var refreshToken = session.getRefreshToken()
      cognitoUser.refreshSession(refreshToken, (error, session) => {
        return (error) ? reject(error) : resolve(session)
      })
    })
  }

  // Cognito user pool credentials dont refresh automatically so
  // we have to refresh them here
  static async refreshCognito () {
    var cognitoUser = this.getCognitoUser()
    var session = await this.getCognitoUserSession(cognitoUser)
    return this.refreshSessionWrapper(cognitoUser, session)
  }

  static globalSignOutWrapper (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.globalSignOut({
        onSuccess: function (result) {
          return resolve(result)
        },
        onFailure: function (error) {
          return reject(error)
        }
      })
    })
  }

  static async logoutCognito () {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.globalSignOutWrapper(cognitoUser)
  }

  static enableMFAWrapper (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.enableMFA(function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async enableMFA () {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.enableMFAWrapper(cognitoUser)
  }

  static disableMFAWrapper (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.disableMFA(function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async disableMFA () {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.disableMFAWrapper(cognitoUser)
  }

  static updateAttributeWrapper (cognitoUser, attributeList) {
    return new Promise((resolve, reject) => {
      cognitoUser.updateAttributes(attributeList, function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async updatePhoneNumber (phoneNumber) {
    var attributeList = []
    var dataPhone = {
      Name: 'phone_number',
      Value: phoneNumber
    }
    var attribute = new CognitoUserAttribute(dataPhone)
    attributeList.push(attribute)

    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.updateAttributeWrapper(cognitoUser, attributeList)
  }

  static getUserAttributesWrapper (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes(function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async getUserAttributes () {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.getUserAttributesWrapper(cognitoUser)
  }

  static deleteUserWrapper (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.deleteUser(function (error, result) {
        return (error) ? reject(error) : resolve(result)
      })
    })
  }

  static async deleteUser () {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.deleteUserWrapper(cognitoUser)
  }

  static async updateUserEmail (email) {
    var attributeList = []
    var dataUserName = {
      Name: 'preferred_username',
      Value: email
    }
    var userNameAttribute = new CognitoUserAttribute(dataUserName)
    attributeList.push(userNameAttribute)

    var dataEmail = {
      Name: 'email',
      Value: email
    }
    var emailAttribute = new CognitoUserAttribute(dataEmail)
    attributeList.push(emailAttribute)

    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.updateAttributeWrapper(cognitoUser, attributeList)
  }

  static verifyAttributeWrapper (cognitoUser, attribute) {
    return new Promise((resolve, reject) => {
      cognitoUser.getAttributeVerificationCode(attribute, {
        onSuccess: function (result) {
          return resolve(result)
        },
        onFailure: function (error) {
          return reject(error)
        },
        inputVerificationCode: function () {
          var verificationCode = prompt('Please input verification code: ', '')
          cognitoUser.verifyAttribute(attribute, verificationCode, {
            onSuccess: function (result) {
              return resolve(result)
            },
            onFailure: function (error) {
              return reject(error)
            }
          })
        }
      })
    })
  }

  static async verifyAttribute (attribute) {
    var cognitoUser = this.getCognitoUser()
    await this.getCognitoUserSession(cognitoUser)
    return this.verifyAttributeWrapper(cognitoUser, attribute)
  }
}



// WEBPACK FOOTER //
// ./src/store/lib/cognitoLogin.js